import { useState } from 'react';
import Link from '@/components/base/TransitionLink';
import { Section, SHELL, LINE, INSET_X } from '@/components/base/Frame';
import SectionLabel from '@/components/base/SectionLabel';
import { services } from '@/mocks/home';
import OperationalWireFigures from './OperationalWireFigures';

export default function Services() {
  const highlighted = [services[0]?.index, services[2]?.index];
  // Single owner of the hover state: at most one row is ever "on".
  const [active, setActive] = useState<string | null>(null);

  return (
    <Section id="services" divider growOnShort>
      <div className={`${SHELL} flex flex-1 flex-col pb-8 pt-8 lg:pb-0`}>
        <div className={`flex flex-col ${INSET_X}`}>
          <SectionLabel index="02" label="Automation + Implementation" />
          <h2 className="reveal-up mt-4 text-3xl font-light leading-[1.1] tracking-[-0.02em] text-foreground-950 lg:whitespace-nowrap lg:text-[clamp(30px,2.9vw,42px)]">
            What we automate — and how we make it work.
          </h2>
        </div>

        <div className="mt-10 flex flex-col lg:mt-auto lg:pt-10">
          <ul className={`border-t ${LINE}`} onPointerLeave={() => setActive(null)}>
            {services.map((s) => {
              const on = active === s.index;
              const hi = highlighted.includes(s.index);
              // Fade only while turning ON; turning OFF is instant, so two rows never fade at once.
              const fade = on ? 'transition-colors duration-200 ease-out' : '';
              return (
                <li
                  key={s.index}
                  onPointerEnter={(e) => { if (e.pointerType === 'mouse') setActive(s.index); }}
                  className={`cursor-target relative grid w-full cursor-pointer grid-cols-[32px_1fr_24px] items-start gap-x-3 md:items-baseline md:gap-x-4 gap-y-1 border-b ${LINE} py-4 ${INSET_X} md:grid-cols-[56px_minmax(0,4fr)_minmax(0,7fr)_24px] md:gap-x-8 lg:py-3 ${hi ? 'bg-primary-50/70' : 'bg-background-50'} before:absolute before:inset-y-0 before:left-0 before:w-px before:bg-primary-400 ${hi && !on ? 'before:opacity-100' : 'before:opacity-0'}`}
                >
                  {/* Dark fill is its own layer: only opacity animates (GPU), no background repaint. */}
                  <span
                    aria-hidden="true"
                    className={`pointer-events-none absolute inset-0 bg-primary-500 will-change-[opacity] ${on ? 'opacity-100 transition-opacity duration-200 ease-out' : 'opacity-0'}`}
                  />
                  {'href' in s && s.href ? (
                    <Link to={s.href} aria-label={`${s.title} — service details`} className="absolute inset-0 z-10 cursor-pointer" />
                  ) : null}
                  <span className={`relative pt-[7px] font-label text-[13px] md:pt-0 font-medium tracking-[0.1em] ${fade} ${on ? 'text-background-50' : 'text-primary-500'}`}>{s.index}</span>
                  <h3 className={`relative text-[22px] leading-tight font-light md:text-2xl tracking-[-0.015em] lg:text-[26px] ${fade} ${on ? 'text-background-50' : 'text-foreground-950'}`}>{s.title}</h3>
                  <p className={`relative col-start-2 col-end-4 text-[15px] md:col-end-auto md:text-base leading-relaxed md:col-start-3 md:row-start-1 ${fade} ${on ? 'text-background-50/90' : 'text-foreground-700'}`}>{s.desc}</p>
                  <span className={`relative col-start-3 row-start-1 mt-[2px] flex h-6 w-6 items-center justify-center justify-self-end text-lg md:mt-0 duration-200 ease-out md:col-start-4 ${on ? 'transition-[transform,color]' : 'transition-transform'} ${on ? 'translate-x-1.5 text-background-50' : 'text-foreground-500'}`}>
                    <i className="ri-arrow-right-line" />
                  </span>
                </li>
              );
            })}
          </ul>

          <div id="approach" className="mt-8 scroll-mt-24 lg:mt-0">
            <OperationalWireFigures tone="light" />
          </div>
        </div>
      </div>
    </Section>
  );
}